import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Grid,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
  isPast,
} from 'date-fns';
import { vi } from 'date-fns/locale';

const PRIORITY_COLORS = {
  low: '#94a3b8',
  medium: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444',
};

const STATUS_COLORS = {
  todo: '#64748b',
  in_progress: '#3b82f6',
  review: '#f59e0b',
  done: '#10b981',
};

const WEEKDAYS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

const TaskCalendarView = ({ tasks, loading, onTaskClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Flatten all tasks
  const allTasks = useMemo(() => {
    if (!tasks) return [];
    return [
      ...(tasks.todo || []),
      ...(tasks.in_progress || []),
      ...(tasks.review || []),
      ...(tasks.done || []),
    ];
  }, [tasks]);

  // Group tasks by deadline date
  const tasksByDate = useMemo(() => {
    const grouped = {};
    allTasks.forEach((task) => {
      if (task.deadline) {
        const dateKey = format(new Date(task.deadline), 'yyyy-MM-dd');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(task);
      }
    });
    return grouped;
  }, [allTasks]);

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const handleToday = () => setCurrentMonth(new Date());

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden',
      }}
    >
      {/* Calendar Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f1f5f9',
          bgcolor: '#f8fafc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevMonth} size="small">
            <PrevIcon />
          </IconButton>
          <IconButton onClick={handleNextMonth} size="small">
            <NextIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
            {format(currentMonth, 'MMMM yyyy', { locale: vi })}
          </Typography>
        </Box>

        <IconButton onClick={handleToday} size="small" sx={{ bgcolor: '#667eea', color: 'white' }}>
          <TodayIcon />
        </IconButton>
      </Box>

      {/* Weekday Headers */}
      <Grid container sx={{ borderBottom: '1px solid #f1f5f9' }}>
        {WEEKDAYS.map((day, index) => (
          <Grid
            item
            xs={12 / 7}
            key={day}
            sx={{
              py: 1,
              textAlign: 'center',
              bgcolor: '#f8fafc',
              borderRight: index < 6 ? '1px solid #f1f5f9' : 'none',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: index === 0 ? '#ef4444' : '#64748b',
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar Grid */}
      <Grid container>
        {calendarDays.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayTasks = tasksByDate[dateKey] || [];
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isTodayDate = isToday(day);
          const hasOverdue = dayTasks.some(
            (t) => t.status !== 'done' && isPast(new Date(t.deadline))
          );

          return (
            <Grid
              item
              xs={12 / 7}
              key={index}
              sx={{
                minHeight: 120,
                p: 1,
                borderRight: (index + 1) % 7 !== 0 ? '1px solid #f1f5f9' : 'none',
                borderBottom: '1px solid #f1f5f9',
                bgcolor: isTodayDate
                  ? '#eff6ff'
                  : !isCurrentMonth
                  ? '#fafafa'
                  : 'white',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#f8fafc',
                },
              }}
            >
              {/* Day Number */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isTodayDate ? 700 : 500,
                    color: !isCurrentMonth
                      ? '#cbd5e1'
                      : isTodayDate
                      ? '#3b82f6'
                      : day.getDay() === 0
                      ? '#ef4444'
                      : '#374151',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isTodayDate ? '#3b82f6' : 'transparent',
                    ...(isTodayDate && { color: 'white' }),
                  }}
                >
                  {format(day, 'd')}
                </Typography>

                {dayTasks.length > 0 && (
                  <Chip
                    label={dayTasks.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: hasOverdue ? '#fee2e2' : '#dbeafe',
                      color: hasOverdue ? '#ef4444' : '#3b82f6',
                    }}
                  />
                )}
              </Box>

              {/* Tasks */}
              <Box sx={{ overflow: 'hidden' }}>
                {dayTasks.slice(0, 3).map((task, taskIndex) => (
                  <Tooltip
                    key={task._id}
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {task.title}
                        </Typography>
                        {task.assignee && (
                          <Typography variant="caption">
                            {task.assignee.fullName}
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      onClick={() => onTaskClick?.(task)}
                      sx={{
                        p: 0.5,
                        mb: 0.5,
                        borderRadius: 1,
                        bgcolor: STATUS_COLORS[task.status] + '20',
                        borderLeft: `3px solid ${PRIORITY_COLORS[task.priority]}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateX(2px)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: task.status === 'done' ? '#64748b' : '#374151',
                          textDecoration: task.status === 'done' ? 'line-through' : 'none',
                        }}
                      >
                        {task.title}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}

                {dayTasks.length > 3 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontSize: '0.65rem',
                      cursor: 'pointer',
                      '&:hover': { color: '#3b82f6' },
                    }}
                  >
                    +{dayTasks.length - 3} task khác
                  </Typography>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Legend */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #f1f5f9',
          display: 'flex',
          gap: 3,
          flexWrap: 'wrap',
          bgcolor: '#f8fafc',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
          Trạng thái:
        </Typography>
        {Object.entries(STATUS_COLORS).map(([key, color]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: color + '40' }} />
            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
              {key === 'in_progress' ? 'Đang làm' : key === 'todo' ? 'Chờ' : key === 'review' ? 'Review' : 'Done'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default TaskCalendarView;
